import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow as RunIcon, Save as SaveIcon } from '@mui/icons-material';
import { aiGovernanceAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { UI_TOKENS } from '../../constants/uiTokens';
import { BTN_SM, CARD_SX, FIELD_SX, PAGE } from '../../constants/pageStyles';
import { getApiErrorMessage } from '../../utils/apiErrors';
import type { AIGovernanceSettings, UpdateAIGovernanceSettingsInput } from '../../types/aiGovernance';

const DEFAULTS: UpdateAIGovernanceSettingsInput = {
  scheduler_enabled: true,
  scheduler_interval_minutes: 60,
  expiry_warning_days: 7,
  recert_interval_days: 90,
  recert_reminder_cooldown_hours: 24,
};

interface Props {
  refreshToken?: number;
}

const AiGovernanceSettingsPanel: React.FC<Props> = ({ refreshToken = 0 }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState<UpdateAIGovernanceSettingsInput>(DEFAULTS);
  const [lastRun, setLastRun] = useState<Record<string, unknown> | null>(null);
  const [expiredGrants, setExpiredGrants] = useState(0);
  const [lastSchedulerRunAt, setLastSchedulerRunAt] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aiGovernanceAPI.getSchedulerStatus();
      const data = res.data.data;
      if (data?.settings) {
        const s = data.settings;
        setForm({
          scheduler_enabled: s.scheduler_enabled,
          scheduler_interval_minutes: s.scheduler_interval_minutes,
          expiry_warning_days: s.expiry_warning_days,
          recert_interval_days: s.recert_interval_days,
          recert_reminder_cooldown_hours: s.recert_reminder_cooldown_hours,
        });
        setLastSchedulerRunAt(s.last_scheduler_run_at);
      }
      setLastRun(data?.latest_run ?? null);
      setExpiredGrants(Number(data?.expired_grants ?? 0));
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to load AI governance settings'));
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await aiGovernanceAPI.updateSettings(form);
      const saved = res.data.data as AIGovernanceSettings | undefined;
      if (saved) {
        setLastSchedulerRunAt(saved.last_scheduler_run_at);
      }
      showSuccess('AI governance settings saved');
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await aiGovernanceAPI.runScheduler();
      const result = res.data.data;
      const parts = [
        result?.expiredCount ? `${result.expiredCount} expired` : null,
        result?.expiryWarningCount ? `${result.expiryWarningCount} warnings` : null,
        result?.recertFlaggedCount ? `${result.recertFlaggedCount} recert flagged` : null,
        result?.recertReminderCount ? `${result.recertReminderCount} reminders` : null,
      ].filter(Boolean);
      showSuccess(parts.length ? `Scheduler run complete: ${parts.join(', ')}` : 'Scheduler run complete (no changes)');
      await load();
    } catch (e: unknown) {
      showError(getApiErrorMessage(e, 'Failed to run scheduler'));
    } finally {
      setRunning(false);
    }
  };

  const setNum = (key: keyof UpdateAIGovernanceSettingsInput, raw: string) => {
    const n = parseInt(raw, 10);
    setForm((prev) => ({ ...prev, [key]: Number.isFinite(n) ? n : '' }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const runSummary = lastRun?.completed_at
    ? `${expiredGrants} expired grant${expiredGrants === 1 ? '' : 's'}. Last run ${new Date(String(lastRun.completed_at)).toLocaleString()} marked ${lastRun.expired_count ?? 0} newly expired and sent ${lastRun.expiry_warning_count ?? 0} warnings.`
    : `${expiredGrants} expired grant${expiredGrants === 1 ? '' : 's'}. No scheduler runs recorded yet for this enterprise.`;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ ...CARD_SX, p: 2.5 }}>
        <Typography sx={PAGE.sectionTitle}>Grant lifecycle scheduler</Typography>
        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.secondary', mb: 2 }}>
          Configure automatic expiry warnings, grant expiration, and periodic recertification for your enterprise.
          These settings apply to all active AI access grants.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={Boolean(form.scheduler_enabled)}
              onChange={(e) => setForm((prev) => ({ ...prev, scheduler_enabled: e.target.checked }))}
            />
          }
          label="Enable automatic grant lifecycle processing"
          sx={{ mb: 2, display: 'block' }}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Run interval (minutes)"
              helperText="15–1440. How often the scheduler checks grants."
              value={form.scheduler_interval_minutes ?? ''}
              onChange={(e) => setNum('scheduler_interval_minutes', e.target.value)}
              sx={FIELD_SX}
              inputProps={{ min: 15, max: 1440 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Expiry warning (days before)"
              helperText="1–90 days before grant expires."
              value={form.expiry_warning_days ?? ''}
              onChange={(e) => setNum('expiry_warning_days', e.target.value)}
              sx={FIELD_SX}
              inputProps={{ min: 1, max: 90 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Recertification interval (days)"
              helperText="30–365 days since last attestation."
              value={form.recert_interval_days ?? ''}
              onChange={(e) => setNum('recert_interval_days', e.target.value)}
              sx={FIELD_SX}
              inputProps={{ min: 30, max: 365 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Recert reminder cooldown (hours)"
              helperText="1–168 hours between pending recert reminders."
              value={form.recert_reminder_cooldown_hours ?? ''}
              onChange={(e) => setNum('recert_reminder_cooldown_hours', e.target.value)}
              sx={FIELD_SX}
              inputProps={{ min: 1, max: 168 }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={saving || running}
            sx={BTN_SM}
          >
            Save settings
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : <RunIcon />}
            onClick={() => void handleRunNow()}
            disabled={saving || running}
            sx={BTN_SM}
          >
            Run scheduler now
          </Button>
        </Stack>
      </Paper>

      <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: UI_TOKENS.text.bodySm } }}>
        {runSummary}
        {lastSchedulerRunAt && (
          <>
            <br />
            Last scheduled tick for this enterprise: {new Date(lastSchedulerRunAt).toLocaleString()}
          </>
        )}
      </Alert>
    </Stack>
  );
};

export default AiGovernanceSettingsPanel;
